import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Chip,
    CircularProgress,
    Alert,
    Divider,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    Tooltip,
    Paper,
    LinearProgress,
} from '@mui/material';
import {
    Add as AddIcon,
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Edit as EditIcon,
    ExpandMore as ExpandMoreIcon,
    WarningAmber as WarningAmberIcon,
    Air as AirIcon,
    Water as WaterIcon,
    AcUnit as AcUnitIcon,
    LocalFireDepartment as FireIcon,
    HelpOutline as OtherIcon,
    Thunderstorm as ThunderstormIcon,
    Thermostat as ThermostatIcon,
    WaterDrop as WaterDropIcon,
    Cloud as CloudIcon,
    CalendarMonth as CalendarMonthIcon,
    Person as PersonIcon,
    Groups as GroupsIcon,
} from '@mui/icons-material';
import { PageHeader, EmptyState } from '../../components/admin';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { type Dayjs } from 'dayjs';
import type { Pohotovost, CreatePohotovostRequest, PohotovostKategorie } from '../../api/JsdhNotifikaceService/generated/models/Pohotovost';
import {
    usePohotovostVsechnyQuery,
    usePohotovostOdpovediQuery,
    useVytvorPohotovostMutation,
    useUpravPohotovostMutation,
    useUzavritPohotovostMutation,
} from '../../api/JsdhNotifikaceService/service/PohotovostMutations';

// HO-36: rozšířeno o 3 nové kategorie (BOURKA/TEPLOTNI_EXTREM/DEST) z meteo mapping awareness_type
const kategorieLabels: Record<string, string> = {
    VITR: 'Vítr',
    POVODNE: 'Povodně',
    SNEHOVA_KALAMITA: 'Sněhová kalamita',
    POZARNI_NEBEZPECI: 'Požární nebezpečí',
    BOURKA: 'Bouřka',
    TEPLOTNI_EXTREM: 'Teplotní extrém',
    DEST: 'Vydatný déšť',
    JINE: 'Jiné',
};

const kategorieColors: Record<string, string> = {
    VITR: '#ed6c02',
    POVODNE: '#1976d2',
    SNEHOVA_KALAMITA: '#0288d1',
    POZARNI_NEBEZPECI: '#d32f2f',
    BOURKA: '#7b1fa2',
    TEPLOTNI_EXTREM: '#f57c00',
    DEST: '#0277bd',
    JINE: '#757575',
};

const KategorieIcon: React.FC<{ kategorie?: string; size?: number }> = ({ kategorie, size = 32 }) => {
    const color = kategorie ? kategorieColors[kategorie] || '#757575' : '#bdbdbd';
    const sx = { color, fontSize: size };
    switch (kategorie) {
        case 'VITR': return <AirIcon sx={sx} />;
        case 'POVODNE': return <WaterIcon sx={sx} />;
        case 'SNEHOVA_KALAMITA': return <AcUnitIcon sx={sx} />;
        case 'POZARNI_NEBEZPECI': return <FireIcon sx={sx} />;
        case 'BOURKA': return <ThunderstormIcon sx={sx} />;
        case 'TEPLOTNI_EXTREM': return <ThermostatIcon sx={sx} />;
        case 'DEST': return <WaterDropIcon sx={sx} />;
        case 'JINE': return <OtherIcon sx={sx} />;
        default: return <WarningAmberIcon sx={sx} />;
    }
};

const formatDateTime = (value: string): string => {
    return new Date(value).toLocaleString('cs-CZ', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const PohotovostPage: React.FC = () => {
    const { data: pohotovosti, isLoading, error } = usePohotovostVsechnyQuery();
    const vytvorMutation = useVytvorPohotovostMutation();
    const upravMutation = useUpravPohotovostMutation();
    const uzavritMutation = useUzavritPohotovostMutation();

    // Create/Edit dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [nazev, setNazev] = useState('');
    const [popis, setPopis] = useState('');
    const [kategorie, setKategorie] = useState<PohotovostKategorie | ''>('');
    const [platnostOd, setPlatnostOd] = useState<Dayjs | null>(null);
    const [platnostDo, setPlatnostDo] = useState<Dayjs | null>(null);

    // Detail dialog
    const [detailId, setDetailId] = useState<number | null>(null);
    const { data: odpovedi } = usePohotovostOdpovediQuery(detailId);

    // Close confirmation
    const [uzavritConfirmId, setUzavritConfirmId] = useState<number | null>(null);
    const uzavritTarget = useMemo(
        () => pohotovosti?.find(p => p.id === uzavritConfirmId),
        [pohotovosti, uzavritConfirmId]
    );

    const aktivni = useMemo(() => pohotovosti?.filter(p => p.aktivni) || [], [pohotovosti]);
    const archiv = useMemo(() => pohotovosti?.filter(p => !p.aktivni) || [], [pohotovosti]);

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingId(null);
        setNazev('');
        setPopis('');
        setKategorie('');
        setPlatnostOd(null);
        setPlatnostDo(null);
    };

    const handleOpenCreate = () => {
        setEditingId(null);
        setNazev('');
        setPopis('');
        setKategorie('');
        setPlatnostOd(null);
        setPlatnostDo(null);
        setDialogOpen(true);
    };

    const handleOpenEdit = (p: Pohotovost) => {
        setEditingId(p.id);
        setNazev(p.nazev);
        setPopis(p.popis || '');
        setKategorie(p.kategorie || '');
        setPlatnostOd(p.platnostOd ? dayjs(p.platnostOd) : null);
        setPlatnostDo(p.platnostDo ? dayjs(p.platnostDo) : null);
        setDialogOpen(true);
    };

    const handleSubmit = () => {
        if (!nazev.trim()) return;
        const data: CreatePohotovostRequest = {
            nazev: nazev.trim(),
            popis: popis.trim() || undefined,
            kategorie: kategorie || undefined,
            platnostOd: platnostOd?.toISOString(),
            platnostDo: platnostDo?.toISOString(),
        };
        if (editingId !== null) {
            upravMutation.mutate({ id: editingId, data }, {
                onSuccess: handleCloseDialog,
            });
        } else {
            vytvorMutation.mutate(data, {
                onSuccess: handleCloseDialog,
            });
        }
    };

    const handleUzavrit = (id: number) => {
        setUzavritConfirmId(id);
    };

    const handleConfirmUzavrit = () => {
        if (uzavritConfirmId !== null) {
            uzavritMutation.mutate(uzavritConfirmId, {
                onSuccess: () => setUzavritConfirmId(null),
            });
        }
    };

    if (error) {
        return <Alert severity="error">Chyba: {error.message}</Alert>;
    }

    return (
        <Box>
            <PageHeader
                title="Pohotovostní výzvy"
                action={
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreate}
                    >
                        Nová výzva
                    </Button>
                }
            />

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Aktivní */}
            {!isLoading && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" color="warning.main" gutterBottom>
                        Aktivní ({aktivni.length})
                    </Typography>
                    {aktivni.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {aktivni.map(p => (
                                <PohotovostCard
                                    key={p.id}
                                    pohotovost={p}
                                    onUzavrit={handleUzavrit}
                                    onDetail={(id) => setDetailId(id)}
                                    onEdit={handleOpenEdit}
                                    isActive
                                />
                            ))}
                        </Box>
                    ) : (
                        <EmptyState
                            icon={<WarningAmberIcon />}
                            title="Žádné aktivní výzvy"
                            description="Vytvořte novou pohotovostní výzvu tlačítkem výše"
                        />
                    )}
                </Box>
            )}

            {/* Archiv */}
            {archiv.length > 0 && (
                <Accordion
                    defaultExpanded={false}
                    disableGutters
                    sx={{ '&:before': { display: 'none' }, boxShadow: 'none' }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" color="text.secondary">
                            Archiv ({archiv.length})
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {archiv.map(p => (
                                <PohotovostCard
                                    key={p.id}
                                    pohotovost={p}
                                    onDetail={(id) => setDetailId(id)}
                                />
                            ))}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            )}

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{editingId !== null ? 'Upravit výzvu' : 'Nová pohotovostní výzva'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
                    <TextField
                        label="Název *"
                        value={nazev}
                        onChange={(e) => setNazev(e.target.value)}
                        fullWidth
                    />
                    <TextField
                        label="Popis"
                        value={popis}
                        onChange={(e) => setPopis(e.target.value)}
                        multiline
                        rows={3}
                        fullWidth
                    />
                    <FormControl fullWidth>
                        <InputLabel>Kategorie</InputLabel>
                        <Select
                            value={kategorie}
                            label="Kategorie"
                            onChange={(e) => setKategorie(e.target.value as PohotovostKategorie | '')}
                        >
                            <MenuItem value="">Bez kategorie</MenuItem>
                            {Object.entries(kategorieLabels).map(([key, label]) => (
                                <MenuItem key={key} value={key}>{label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <DateTimePicker
                        label="Platnost od (volitelné)"
                        value={platnostOd}
                        onChange={(val) => {
                            setPlatnostOd(val);
                            if (val && platnostDo && platnostDo.isBefore(val)) {
                                setPlatnostDo(null);
                            }
                        }}
                        ampm={false}
                    />
                    <DateTimePicker
                        label="Platnost do (volitelné)"
                        value={platnostDo}
                        onChange={setPlatnostDo}
                        ampm={false}
                        minDateTime={platnostOd || undefined}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Zrušit</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!nazev.trim() || vytvorMutation.isPending || upravMutation.isPending}
                    >
                        {editingId !== null ? 'Uložit' : 'Vytvořit'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Detail Dialog */}
            <Dialog open={detailId !== null} onClose={() => setDetailId(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Odpovědi na výzvu</DialogTitle>
                <DialogContent>
                    {odpovedi && odpovedi.length > 0 ? (
                        <Box>
                            {/* Summary bar */}
                            {(() => {
                                const dCount = odpovedi.filter(o => o.odpoved === 'DOSTUPNY').length;
                                const nCount = odpovedi.filter(o => o.odpoved === 'NEDOSTUPNY').length;
                                const total = dCount + nCount;
                                return (
                                    <Box sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Chip
                                                icon={<CheckCircleIcon />}
                                                label={`${dCount} dostupných`}
                                                color="success"
                                                size="small"
                                            />
                                            <Chip
                                                icon={<CancelIcon />}
                                                label={`${nCount} nedostupných`}
                                                color="error"
                                                size="small"
                                            />
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={total > 0 ? (dCount / total) * 100 : 0}
                                            sx={{
                                                height: 8,
                                                borderRadius: 4,
                                                backgroundColor: 'error.light',
                                                '& .MuiLinearProgress-bar': {
                                                    backgroundColor: 'success.main',
                                                    borderRadius: 4,
                                                },
                                            }}
                                        />
                                    </Box>
                                );
                            })()}
                            <Divider sx={{ mb: 1 }} />
                            {/* Dostupní first, then nedostupní */}
                            {[...odpovedi]
                                .sort((a, b) => (a.odpoved === 'DOSTUPNY' ? -1 : 1) - (b.odpoved === 'DOSTUPNY' ? -1 : 1))
                                .map(o => (
                                <Box
                                    key={o.id}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        py: 1,
                                        px: 1,
                                        borderRadius: 1,
                                        '&:hover': { backgroundColor: 'action.hover' },
                                    }}
                                >
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography variant="body2" fontWeight={500}>
                                            {o.jmeno || `Uživatel #${o.uzivatelId}`}
                                        </Typography>
                                        {o.funkce && (
                                            <Typography variant="caption" color="text.secondary">
                                                {typeof o.funkce === 'string'
                                                    ? o.funkce
                                                    : Array.isArray(o.funkce)
                                                        ? (o.funkce as any[]).map(f => typeof f === 'string' ? f : f?.kod || '').join(', ')
                                                        : typeof o.funkce === 'object' && o.funkce !== null
                                                            ? (o.funkce as any).kod || ''
                                                            : String(o.funkce)}
                                            </Typography>
                                        )}
                                        {o.poznamka && (
                                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block' }}>
                                                „{o.poznamka}"
                                            </Typography>
                                        )}
                                        {o.cas && (
                                            <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                                                {formatDateTime(o.cas)}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Chip
                                        icon={o.odpoved === 'DOSTUPNY' ? <CheckCircleIcon /> : <CancelIcon />}
                                        label={o.odpoved === 'DOSTUPNY' ? 'Dostupný' : 'Nedostupný'}
                                        color={o.odpoved === 'DOSTUPNY' ? 'success' : 'error'}
                                        size="small"
                                        variant="outlined"
                                    />
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <EmptyState
                            icon={<GroupsIcon />}
                            title="Zatím žádné odpovědi"
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailId(null)}>Zavřít</Button>
                </DialogActions>
            </Dialog>

            {/* Close Confirmation Dialog */}
            <Dialog open={uzavritConfirmId !== null} onClose={() => setUzavritConfirmId(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {uzavritTarget?.source === 'AUTO_METEO'
                        ? 'Uzavřít automatickou pohotovost?'
                        : 'Uzavřít výzvu?'}
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Opravdu chcete uzavřít výzvu <strong>{uzavritTarget?.nazev}</strong>?
                    </Typography>
                    {/* HO-37 N1: Confirmation dialog pro manual close AUTO_METEO (5. kolo H4) */}
                    {uzavritTarget?.source === 'AUTO_METEO' && (
                        <Alert severity="warning" icon={<CloudIcon />} sx={{ mt: 2 }}>
                            Tato pohotovost vznikla automaticky z ČHMÚ výstrahy.
                            {uzavritTarget.platnostDo && (
                                <> ČHMÚ výstraha <strong>stále platí</strong> (do{' '}
                                {new Date(uzavritTarget.platnostDo).toLocaleString('cs-CZ')}).</>
                            )}
                            <br /><br />
                            Po uzavření se pohotovost <strong>znovu nevyhlasí</strong>, dokud:
                            <ul>
                                <li>výstraha skončí (ČHMÚ pošle CANCEL)</li>
                                <li>nebo ji znovu povolíte v sekci <strong>Konfigurace → Meteo</strong>
                                {' '}(odeberte a znovu přidejte odpovídající ORP)</li>
                            </ul>
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUzavritConfirmId(null)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleConfirmUzavrit}
                        disabled={uzavritMutation.isPending}
                    >
                        Uzavřít
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

interface PohotovostCardProps {
    pohotovost: Pohotovost;
    onUzavrit?: (id: number) => void;
    onDetail: (id: number) => void;
    onEdit?: (p: Pohotovost) => void;
    isActive?: boolean;
}

const PohotovostCard: React.FC<PohotovostCardProps> = ({ pohotovost, onUzavrit, onDetail, onEdit, isActive }) => {
    const { data: odpovedi } = usePohotovostOdpovediQuery(isActive ? pohotovost.id : null);
    const dostupnyCount = odpovedi?.filter(o => o.odpoved === 'DOSTUPNY').length ?? 0;
    const nedostupnyCount = odpovedi?.filter(o => o.odpoved === 'NEDOSTUPNY').length ?? 0;
    const totalCount = dostupnyCount + nedostupnyCount;
    const borderColor = pohotovost.kategorie
        ? kategorieColors[pohotovost.kategorie] || '#bdbdbd'
        : isActive ? '#ed6c02' : '#bdbdbd';

    return (
        <Paper
            sx={{
                p: 0,
                display: 'flex',
                overflow: 'hidden',
                opacity: isActive ? 1 : 0.75,
                transition: 'box-shadow 0.2s, opacity 0.2s',
                '&:hover': {
                    boxShadow: isActive ? 4 : 2,
                    opacity: 1,
                },
            }}
            elevation={isActive ? 2 : 0}
            variant={isActive ? 'elevation' : 'outlined'}
        >
            {/* Left color strip + icon */}
            <Box
                sx={{
                    width: { xs: 44, sm: 56 },
                    minHeight: '100%',
                    backgroundColor: `${borderColor}14`,
                    borderRight: `3px solid ${borderColor}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                <KategorieIcon kategorie={pohotovost.kategorie} size={28} />
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, py: 1.5, px: { xs: 1.5, sm: 2 }, minWidth: 0 }}>
                {/* Title row */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 0.5 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
                        {pohotovost.nazev}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                        {pohotovost.kategorie && (
                            <Chip
                                label={kategorieLabels[pohotovost.kategorie] || pohotovost.kategorie}
                                size="small"
                                sx={{
                                    backgroundColor: `${kategorieColors[pohotovost.kategorie]}18`,
                                    color: kategorieColors[pohotovost.kategorie],
                                    fontWeight: 500,
                                }}
                            />
                        )}
                        <Chip
                            label={pohotovost.aktivni ? 'Aktivní' : 'Uzavřená'}
                            color={pohotovost.aktivni ? 'success' : 'default'}
                            size="small"
                            variant={pohotovost.aktivni ? 'filled' : 'outlined'}
                        />
                    </Box>
                </Box>

                {/* Description */}
                {pohotovost.popis && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {pohotovost.popis}
                    </Typography>
                )}

                {/* Metadata row */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 2 }, mt: 0.75, color: 'text.secondary' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarMonthIcon sx={{ fontSize: 15 }} />
                        <Typography variant="caption">
                            {formatDateTime(pohotovost.platnostOd)}
                            {pohotovost.platnostDo && ` – ${formatDateTime(pohotovost.platnostDo)}`}
                        </Typography>
                    </Box>
                    {pohotovost.vytvorilJmeno && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonIcon sx={{ fontSize: 15 }} />
                            <Typography variant="caption">{pohotovost.vytvorilJmeno}</Typography>
                        </Box>
                    )}
                </Box>

                {/* Response summary for active cards */}
                {isActive && totalCount > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <GroupsIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                {totalCount} {totalCount === 1 ? 'odpověď' : totalCount < 5 ? 'odpovědi' : 'odpovědí'}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                                variant="determinate"
                                value={totalCount > 0 ? (dostupnyCount / totalCount) * 100 : 0}
                                sx={{
                                    flex: 1,
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: 'error.light',
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: 'success.main',
                                        borderRadius: 4,
                                    },
                                }}
                            />
                            <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
                                <Chip
                                    icon={<CheckCircleIcon />}
                                    label={dostupnyCount}
                                    color="success"
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontWeight: 600 }}
                                />
                                <Chip
                                    icon={<CancelIcon />}
                                    label={nedostupnyCount}
                                    color="error"
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontWeight: 600 }}
                                />
                            </Box>
                        </Box>
                    </Box>
                )}

                {isActive && totalCount === 0 && (
                    <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
                        Zatím žádné odpovědi
                    </Typography>
                )}

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                    <Button size="small" onClick={() => onDetail(pohotovost.id)}>
                        Odpovědi
                    </Button>
                    {isActive && onEdit && (
                        <Tooltip title="Upravit">
                            <IconButton size="small" onClick={() => onEdit(pohotovost)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {isActive && onUzavrit && (
                        <Button
                            size="small"
                            color="warning"
                            startIcon={<CloseIcon />}
                            onClick={() => onUzavrit(pohotovost.id)}
                        >
                            Uzavřít
                        </Button>
                    )}
                </Box>
            </Box>
        </Paper>
    );
};

export default PohotovostPage;
